// "Jump on mouse wheel": every wheel notch is one fresh jump press in the sampled input ticks. The
// sim detects a press by comparing consecutive ticks, so a notch is down for exactly one tick and
// the tick after it is always up: notches are separate edges even when they arrive back to back.

import { Buttons } from "../wasm/types";

/**
 * Notch filter gap of the jump wheel (ms). Spinning the wheel is how a bunny hop is timed onto the
 * landing tick, so unlike weapon switching every notch of a fast wheel counts.
 */
export const WHEEL_JUMP_MIN_GAP_MS = 0;

/**
 * Notches waiting for a tick. A tick takes at most one press and needs an up tick after it, so a
 * burst bigger than this only delays presses (a free-spinning wheel would keep the player
 * jumping long after the wheel stopped).
 */
export const MAX_QUEUED_NOTCHES = 2;

export class WheelJump {
  private queued = 0;
  /** The jump press was delivered by the last `consume`: the next tick must be up. */
  private down = false;

  /** A wheel notch happened. It is delivered by the next `consume` that is allowed to press. */
  notch(): void {
    if (this.queued < MAX_QUEUED_NOTCHES) this.queued++;
  }

  /**
   * Call exactly once per sampled input tick. True when the wheel presses jump in this tick:
   * the first tick after a notch, never two ticks in a row.
   */
  consume(): boolean {
    if (this.down) {
      this.down = false;
      return false;
    }
    if (this.queued === 0) return false;
    this.queued--;
    this.down = true;
    return true;
  }

  /**
   * `buttons` of the tick with the wheel press added. Only ever sets the jump bit, so a held
   * Space (or any other jump binding) stays down through a notch.
   */
  apply(buttons: number): number {
    return this.consume() ? buttons | Buttons.JUMP : buttons;
  }

  /** Forget pending notches (focus loss, pause, respawn, setting turned off). */
  reset(): void {
    this.queued = 0;
    this.down = false;
  }
}
